import { computed, readonly, ref } from 'vue'
import { defineStore } from 'pinia'
import { adminApi, dataSourceMode } from '@/services/adminApi'
import { clearAdminToken, readAdminToken, saveAdminToken } from '@/services/authToken'
import type {
  ActivityItem,
  AdminPaginationMeta,
  ListActivitiesQuery,
  ListWarungsQuery,
  AdminUser,
  CreateWarungInput,
  DashboardData,
  GoogleLoginInput,
  HealthStatus,
  LoginInput,
  ProgramSettings,
  SubscriptionSummary,
  SyncOverview,
  UpdateSubscriptionInput,
  UpdateWarungInput,
  WarungDetail,
  WarungSummary,
} from '@/types/admin'
import type { ToastMessage } from '@/types/ui'
import { createUiId } from '@/utils/id'

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Terjadi kesalahan yang tidak dikenali.'
}

export const useAdminStore = defineStore('admin', () => {
  const authInitialized = ref(false)
  const initialized = ref(false)
  const currentUser = ref<AdminUser | null>(null)
  const loadingKeys = ref(new Set<string>())
  const health = ref<HealthStatus | null>(null)
  const dashboard = ref<DashboardData | null>(null)
  const warungs = ref<readonly WarungSummary[]>([])
  const warungsMeta = ref<AdminPaginationMeta>({ page: 1, perPage: 20, total: 0, totalPages: 1 })
  const warungQuery = ref<ListWarungsQuery>({ page: 1, perPage: 20 })
  const selectedWarung = ref<WarungDetail | null>(null)
  const syncOverview = ref<SyncOverview | null>(null)
  const settings = ref<ProgramSettings | null>(null)
  const activities = ref<readonly ActivityItem[]>([])
  const activitiesMeta = ref<AdminPaginationMeta>({ page: 1, perPage: 20, total: 0, totalPages: 1 })
  const activityQuery = ref<ListActivitiesQuery>({ page: 1, perPage: 20 })
  const toasts = ref<readonly ToastMessage[]>([])

  const isLoading = (key: string): boolean => loadingKeys.value.has(key)
  const activeWarungs = computed(() => warungs.value.filter((warung) => warung.status === 'active').length)
  const attentionWarungs = computed(() => warungs.value.filter((warung) => warung.status === 'attention').length)
  const apiConnected = computed(() => health.value?.ok === true)
  const authenticated = computed(() => currentUser.value !== null)
  const activationCodes = ref<Readonly<Record<string, string>>>({})
  const webAccess = ref<Readonly<Record<string, { readonly username: string; readonly email: string | null; readonly temporaryPassword: string }>>>({})

  function begin(key: string): void {
    loadingKeys.value = new Set(loadingKeys.value).add(key)
  }

  function end(key: string): void {
    const next = new Set(loadingKeys.value)
    next.delete(key)
    loadingKeys.value = next
  }

  function toast(tone: ToastMessage['tone'], title: string, message: string): void {
    const item: ToastMessage = { id: createUiId('toast'), tone, title, message }
    toasts.value = [...toasts.value, item]
    globalThis.setTimeout(() => dismissToast(item.id), 5_000)
  }

  function dismissToast(id: string): void {
    toasts.value = toasts.value.filter((item) => item.id !== id)
  }

  async function run<T>(key: string, task: () => Promise<T>): Promise<T> {
    begin(key)
    try {
      return await task()
    } finally {
      end(key)
    }
  }

  async function initializeAuth(): Promise<void> {
    if (authInitialized.value) return
    try {
      if (dataSourceMode === 'api' && readAdminToken() === null) {
        currentUser.value = null
      } else {
        currentUser.value = await adminApi.me()
      }
    } catch {
      clearAdminToken()
      currentUser.value = null
    } finally {
      authInitialized.value = true
    }
  }

  async function login(input: LoginInput): Promise<void> {
    try {
      const session = await run('login', () => adminApi.login(input))
      saveAdminToken(session.token)
      currentUser.value = session.user
      initialized.value = false
      await initialize()
    } catch (error: unknown) {
      toast('error', 'Login gagal', errorMessage(error))
      throw error
    }
  }

  async function loginWithGoogle(input: GoogleLoginInput): Promise<void> {
    try {
      const session = await run('google-login', () => adminApi.loginWithGoogle(input))
      saveAdminToken(session.token)
      currentUser.value = session.user
      initialized.value = false
      await initialize()
    } catch (error: unknown) {
      toast('error', 'Login Google gagal', errorMessage(error))
      throw error
    }
  }

  async function logout(): Promise<void> {
    try {
      if (currentUser.value !== null) await adminApi.logout()
    } catch {
      // Sesi lokal tetap dibersihkan walaupun server tidak dapat dijangkau.
    } finally {
      clearAdminToken()
      currentUser.value = null
      initialized.value = false
      dashboard.value = null
      warungs.value = []
      warungsMeta.value = { page: 1, perPage: 20, total: 0, totalPages: 1 }
      selectedWarung.value = null
      syncOverview.value = null
      settings.value = null
      activities.value = []
      activitiesMeta.value = { page: 1, perPage: 20, total: 0, totalPages: 1 }
    }
  }

  function activationCodeFor(warungId: string): string | null {
    return activationCodes.value[warungId] ?? null
  }

  function webAccessFor(warungId: string): { readonly username: string; readonly email: string | null; readonly temporaryPassword: string } | null {
    return webAccess.value[warungId] ?? null
  }

  async function checkHealth(showError = false): Promise<void> {
    try {
      health.value = await run('health', () => adminApi.health())
    } catch (error: unknown) {
      health.value = {
        ok: false,
        service: dataSourceMode === 'api' ? 'admin-api' : 'mock-admin-api',
        version: 'tidak diketahui',
        checkedAt: new Date().toISOString(),
        message: errorMessage(error),
      }
      if (showError) toast('error', 'Server belum terhubung', errorMessage(error))
    }
  }

  async function loadDashboard(): Promise<void> {
    try {
      dashboard.value = await run('dashboard', () => adminApi.getDashboard())
    } catch (error: unknown) {
      toast('error', 'Ringkasan gagal dimuat', errorMessage(error))
      throw error
    }
  }

  async function loadWarungs(query: ListWarungsQuery = warungQuery.value): Promise<void> {
    try {
      const normalized: ListWarungsQuery = { ...query, page: query.page ?? 1, perPage: query.perPage ?? 20 }
      const result = await run('warungs', () => adminApi.listWarungs(normalized))
      warungs.value = result.items
      warungsMeta.value = result.meta
      warungQuery.value = normalized
    } catch (error: unknown) {
      toast('error', 'Daftar warung gagal dimuat', errorMessage(error))
      throw error
    }
  }

  async function loadWarung(id: string): Promise<void> {
    selectedWarung.value = null
    try {
      selectedWarung.value = await run('warung-detail', () => adminApi.getWarung(id))
    } catch (error: unknown) {
      toast('error', 'Detail warung gagal dimuat', errorMessage(error))
      throw error
    }
  }

  async function createWarung(input: CreateWarungInput): Promise<WarungDetail> {
    try {
      const created = await run('create-warung', () => adminApi.createWarung(input))
      if (created.activationCode !== undefined) activationCodes.value = { ...activationCodes.value, [created.id]: created.activationCode }
      await Promise.all([loadWarungs(), loadDashboard(), loadActivities()])
      toast('success', 'Warung ditambahkan', `${created.name} siap memasuki tahap onboarding.`)
      return created
    } catch (error: unknown) {
      toast('error', 'Warung belum dapat ditambahkan', errorMessage(error))
      throw error
    }
  }

  async function updateWarung(id: string, input: UpdateWarungInput): Promise<WarungDetail> {
    try {
      const updated = await run('update-warung', () => adminApi.updateWarung(id, input))
      selectedWarung.value = updated
      await Promise.all([loadWarungs(), loadDashboard(), loadActivities()])
      toast('success', 'Perubahan disimpan', `Data ${updated.name} telah diperbarui.`)
      return updated
    } catch (error: unknown) {
      toast('error', 'Perubahan gagal disimpan', errorMessage(error))
      throw error
    }
  }

  async function generateActivationCode(id: string): Promise<string> {
    try {
      const result = await run(`activation-code-${id}`, () => adminApi.generateActivationCode(id))
      activationCodes.value = { ...activationCodes.value, [id]: result.activationCode }
      await Promise.all([loadWarung(id), loadActivities()])
      toast('success', 'Kode aktivasi dibuat', 'Kode hanya ditampilkan pada sesi ini. Salin dan berikan kepada pemilik warung.')
      return result.activationCode
    } catch (error: unknown) {
      toast('error', 'Kode aktivasi gagal dibuat', errorMessage(error))
      throw error
    }
  }

  async function generateWebAccess(id: string, email: string): Promise<void> {
    try {
      const normalizedEmail = email.trim().toLocaleLowerCase('id-ID')
      const result = await run(`web-access-${id}`, () => adminApi.generateWebAccess(id, normalizedEmail.length > 0 ? { email: normalizedEmail } : {}))
      webAccess.value = { ...webAccess.value, [id]: { username: result.username, email: result.email, temporaryPassword: result.temporaryPassword } }
      toast('success', 'Akses web dibuat', 'Salin kredensial sementara dan berikan kepada pemilik warung.')
      await loadActivities()
    } catch (error: unknown) {
      toast('error', 'Akses web gagal dibuat', errorMessage(error))
      throw error
    }
  }

  async function updateSubscription(id: string, input: UpdateSubscriptionInput): Promise<SubscriptionSummary> {
    try {
      const subscription = await run(`subscription-${id}`, () => adminApi.updateSubscription(id, input))
      if (selectedWarung.value !== null && selectedWarung.value.id === id) selectedWarung.value = { ...selectedWarung.value, subscription }
      toast('success', 'Paket diperbarui', `Paket ${subscription.plan.name} telah diterapkan.`)
      await loadActivities()
      return subscription
    } catch (error: unknown) {
      toast('error', 'Paket belum dapat diperbarui', errorMessage(error))
      throw error
    }
  }

  async function loadSyncOverview(): Promise<void> {
    try {
      syncOverview.value = await run('sync', () => adminApi.getSyncOverview())
    } catch (error: unknown) {
      toast('error', 'Status sinkronisasi gagal dimuat', errorMessage(error))
      throw error
    }
  }

  async function retrySync(warungId?: string): Promise<void> {
    try {
      syncOverview.value = await run(`retry-sync-${warungId ?? 'all'}`, () => adminApi.retrySync(warungId))
      await Promise.all([loadWarungs(), loadDashboard(), loadActivities()])
      toast('success', 'Sinkronisasi diperiksa', warungId === undefined ? 'Seluruh antrean telah dicoba ulang.' : 'Antrean warung telah dicoba ulang.')
    } catch (error: unknown) {
      toast('error', 'Sinkronisasi gagal dicoba ulang', errorMessage(error))
      throw error
    }
  }

  async function loadSettings(): Promise<void> {
    try {
      settings.value = await run('settings', () => adminApi.getSettings())
    } catch (error: unknown) {
      toast('error', 'Pengaturan gagal dimuat', errorMessage(error))
      throw error
    }
  }

  async function saveSettings(input: ProgramSettings): Promise<void> {
    try {
      settings.value = await run('save-settings', () => adminApi.saveSettings(input))
      await loadActivities()
      toast('success', 'Pengaturan disimpan', 'Identitas dan kebijakan program telah diperbarui.')
    } catch (error: unknown) {
      toast('error', 'Pengaturan gagal disimpan', errorMessage(error))
      throw error
    }
  }

  async function loadActivities(query: ListActivitiesQuery = activityQuery.value): Promise<void> {
    try {
      const normalized: ListActivitiesQuery = { ...query, page: query.page ?? 1, perPage: query.perPage ?? 20 }
      const result = await run('activities', () => adminApi.listActivities(normalized))
      activities.value = result.items
      activitiesMeta.value = result.meta
      activityQuery.value = normalized
    } catch (error: unknown) {
      toast('error', 'Aktivitas gagal dimuat', errorMessage(error))
      throw error
    }
  }

  async function initialize(): Promise<void> {
    await initializeAuth()
    if (initialized.value || currentUser.value === null) return
    begin('initialize')
    try {
      await checkHealth()
      const results = await Promise.allSettled([
        adminApi.getDashboard(),
        adminApi.listWarungs({ page: 1, perPage: 20 }),
        adminApi.getSyncOverview(),
        adminApi.getSettings(),
        adminApi.listActivities({ page: 1, perPage: 20 }),
      ])

      const [dashboardResult, warungsResult, syncResult, settingsResult, activitiesResult] = results
      if (dashboardResult?.status === 'fulfilled') dashboard.value = dashboardResult.value
      if (warungsResult?.status === 'fulfilled') { warungs.value = warungsResult.value.items; warungsMeta.value = warungsResult.value.meta }
      if (syncResult?.status === 'fulfilled') syncOverview.value = syncResult.value
      if (settingsResult?.status === 'fulfilled') settings.value = settingsResult.value
      if (activitiesResult?.status === 'fulfilled') { activities.value = activitiesResult.value.items; activitiesMeta.value = activitiesResult.value.meta }

      const rejected = results.filter((result) => result.status === 'rejected')
      if (rejected.length > 0) {
        toast('error', 'Sebagian data belum dimuat', `${rejected.length} permintaan gagal. Coba muat ulang halaman terkait.`)
      }
      initialized.value = true
    } finally {
      end('initialize')
    }
  }

  return {
    dataSourceMode,
    authInitialized: readonly(authInitialized),
    initialized: readonly(initialized),
    currentUser: readonly(currentUser),
    health: readonly(health),
    dashboard: readonly(dashboard),
    warungs: readonly(warungs),
    warungsMeta: readonly(warungsMeta),
    selectedWarung: readonly(selectedWarung),
    syncOverview: readonly(syncOverview),
    settings: readonly(settings),
    activities: readonly(activities),
    activitiesMeta: readonly(activitiesMeta),
    toasts: readonly(toasts),
    activeWarungs,
    attentionWarungs,
    apiConnected,
    authenticated,
    isLoading,
    initializeAuth,
    login,
    loginWithGoogle,
    logout,
    activationCodeFor,
    webAccessFor,
    generateWebAccess,
    updateSubscription,
    initialize,
    checkHealth,
    loadDashboard,
    loadWarungs,
    loadWarung,
    createWarung,
    updateWarung,
    generateActivationCode,
    loadSyncOverview,
    retrySync,
    loadSettings,
    saveSettings,
    loadActivities,
    dismissToast,
  }
})
